/**
 * Workspace switcher.
 *
 * There was no way to change workspace anywhere in the app: `setWorkspaceId`
 * was only ever called at login, signup and invite-accept, so a merchant who
 * belonged to two workspaces had to log out and back in to reach the second
 * one — and accepting an invite silently moved them without saying so.
 *
 * Renders nothing at all for the common single-workspace case, so the account
 * menu is unchanged for most merchants.
 */

import React, { useEffect, useState } from 'react';
import { Check, Building2, Loader2 } from 'lucide-react';
import * as api from '../../services/api';
import { workspacesApi } from '../../services/api';
import { logger } from '../../lib/logger';
import { t } from '../../i18n';

/** Only what this menu renders — the list endpoint returns a leaner Workspace
 *  than the full model type, and the switcher has no use for the rest. */
interface SwitchableWorkspace {
  id: string;
  name: string;
}

interface WorkspaceSwitcherProps {
  /** Close the surrounding account menu before we navigate away. */
  onBeforeSwitch: () => void;
}

export const WorkspaceSwitcher: React.FC<WorkspaceSwitcherProps> = ({ onBeforeSwitch }) => {
  const [workspaces, setWorkspaces] = useState<SwitchableWorkspace[]>([]);
  const [loading, setLoading] = useState(true);
  const [switchingId, setSwitchingId] = useState(null as string | null);

  const currentId = api.getWorkspaceId();

  useEffect(() => {
    let active = true;
    workspacesApi
      .list()
      .then((res) => {
        if (!active) return;
        setWorkspaces(Array.isArray(res?.data) ? res.data : []);
      })
      .catch((err) => {
        // A switcher that can't load its list simply doesn't render — this is
        // never worth blocking or alarming the merchant over.
        logger.debug('[WorkspaceSwitcher] could not load workspaces', err);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  const switchTo = (workspaceId: string): void => {
    if (workspaceId === currentId) return;
    setSwitchingId(workspaceId);
    onBeforeSwitch();
    api.setWorkspaceId(workspaceId);
    // Full reload rather than SPA navigation: every store and in-flight query
    // belongs to the old workspace, and a hard boot is the one thing that
    // cannot leave a stale list from the previous tenant on screen.
    window.location.assign('/business');
  };

  // Nothing to switch between — stay invisible.
  if (loading || workspaces.length < 2) return null;

  return (
    <>
      <div className="border-t border-gray-100 my-1" />
      <div className="px-4 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
        {t('workspaceSwitcher.heading')}
      </div>
      {workspaces.map((ws) => {
        const isCurrent = ws.id === currentId;
        return (
          <button
            key={ws.id}
            type="button"
            onClick={() => switchTo(ws.id)}
            disabled={switchingId !== null}
            aria-current={isCurrent ? 'true' : undefined}
            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors disabled:opacity-60 ${
              isCurrent ? 'text-gray-900 font-medium' : 'text-gray-700 hover:bg-gray-50'
            }`}
          >
            {switchingId === ws.id ? (
              <Loader2 className="w-4 h-4 text-gray-400 animate-spin flex-shrink-0" />
            ) : (
              <Building2 className="w-4 h-4 text-gray-400 flex-shrink-0" />
            )}
            <span className="truncate flex-1 text-left">{ws.name}</span>
            {isCurrent && <Check className="w-4 h-4 text-gray-900 flex-shrink-0" />}
          </button>
        );
      })}
    </>
  );
};
